import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, UserPlus, PhoneCall, ClipboardList, MonitorPlay,
  Sparkles, Trophy, DollarSign, TrendingUp, CalendarDays,
  CalendarClock, FileSearch, ArrowUpRight, Plus,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "@/lib/apiClient";

const CARDS = [
  { key: "total_leads", label: "Total leads", icon: Users, testid: "card-total-leads" },
  { key: "new_leads", label: "New leads", icon: UserPlus, testid: "card-new-leads" },
  { key: "leads_contacted", label: "Contacted", icon: PhoneCall, testid: "card-contacted" },
  { key: "follow_ups_due", label: "Follow-ups due", icon: ClipboardList, testid: "card-followups" },
  { key: "demos_sent", label: "Demos sent", icon: MonitorPlay, testid: "card-demos" },
  { key: "interested_prospects", label: "Interested", icon: Sparkles, testid: "card-interested" },
  { key: "closed_clients", label: "Closed clients", icon: Trophy, testid: "card-closed" },
  { key: "total_pipeline_value", label: "Pipeline value", icon: DollarSign, testid: "card-pipeline", format: "money" },
  { key: "conversion_rate", label: "Conversion rate", icon: TrendingUp, testid: "card-conversion", format: "percent" },
  { key: "this_week", label: "This week", icon: CalendarDays, testid: "card-week" },
  { key: "this_month", label: "This month", icon: CalendarClock, testid: "card-month" },
  { key: "audits_in_flight", label: "Audits in-flight", icon: FileSearch, testid: "card-audits" },
];

const fmt = (v, mode) => {
  if (v == null) return "—";
  if (mode === "money") return `$${Number(v).toLocaleString()}`;
  if (mode === "percent") return `${Number(v).toFixed(1)}%`;
  return Number(v).toLocaleString();
};

const COLORS = ["#3B82F6", "#60A5FA", "#93C5FD", "#22D3EE", "#A78BFA", "#F472B6", "#FBBF24", "#4ADE80"];

export default function Overview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/overview");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) {
    return (
      <div data-testid="overview-loading" className="text-white/50 text-sm">
        Loading overview…
      </div>
    );
  }

  const status = data.status_breakdown || [];
  const sources = data.source_breakdown || [];

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="text-[11px] tracking-[0.22em] uppercase text-blue-400 font-medium">
            Dashboard
          </span>
          <h1 className="font-display text-3xl md:text-4xl mt-2">Overview</h1>
          <p className="text-white/50 text-sm mt-1">
            Real-time CRM snapshot pulled directly from your leads database.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/dashboard/lead-generator"
            className="inline-flex items-center gap-2 text-sm rounded-full border border-white/15 hover:border-white/30 px-4 py-2 text-white/80 hover:text-white transition-colors"
            data-testid="quick-generate"
          >
            <Sparkles size={14} className="text-blue-400" />
            Generate new leads
          </Link>
          <Link
            to="/admin/dashboard/leads?new=1"
            className="inline-flex items-center gap-2 text-sm rounded-full bg-blue-500 hover:bg-blue-600 px-4 py-2 text-white transition-colors"
            data-testid="quick-add-lead"
          >
            <Plus size={14} />
            Add lead manually
          </Link>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.key}
              data-testid={c.testid}
              className="card-soft rounded-xl p-5 transition-all hover:-translate-y-0.5"
            >
              <div className="flex items-start justify-between">
                <span className="h-9 w-9 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center text-blue-300">
                  <Icon size={16} />
                </span>
                <span className="text-[10px] tracking-[0.22em] uppercase text-white/30">
                  live
                </span>
              </div>
              <div className="mt-4 font-display text-2xl md:text-3xl text-white">
                {fmt(data.cards[c.key], c.format)}
              </div>
              <div className="text-xs text-white/50 mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-soft rounded-2xl p-6" data-testid="chart-status">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-blue-400 font-medium">
                Pipeline
              </div>
              <h3 className="font-display text-xl text-white mt-1">Lead status breakdown</h3>
            </div>
          </div>
          {status.length === 0 ? (
            <EmptyChart label="No status data yet — add or import leads to see the breakdown." />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={status}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                <XAxis dataKey="status" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} />
                <Bar dataKey="count" fill="#3B82F6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card-soft rounded-2xl p-6" data-testid="chart-source">
          <div className="text-[11px] tracking-[0.22em] uppercase text-blue-400 font-medium">
            Sources
          </div>
          <h3 className="font-display text-xl text-white mt-1">Where leads are coming from</h3>
          {sources.length === 0 ? (
            <EmptyChart label="No sources tracked yet." />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={sources} dataKey="count" nameKey="source" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {sources.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Legend wrapperStyle={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Activity + follow-ups + audit requests */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ListPanel
          title="Recent lead activity"
          testid="recent-leads"
          empty="No leads yet. Add one manually or wait for the form to fill up."
          items={data.recent_leads}
          renderItem={(l) => (
            <Link to={`/admin/dashboard/leads/${l.id}`} className="block">
              <div className="text-sm text-white truncate">{l.business_name || l.contact_name || l.email || "Untitled"}</div>
              <div className="text-xs text-white/45 mt-0.5">
                {(l.niche || "—") + " · " + (l.status || "New Lead")}
              </div>
            </Link>
          )}
        />
        <ListPanel
          title="Upcoming follow-ups"
          testid="upcoming-followups"
          empty="No scheduled follow-ups."
          items={data.upcoming_follow_ups}
          renderItem={(l) => (
            <Link to={`/admin/dashboard/leads/${l.id}`} className="block">
              <div className="text-sm text-white truncate">{l.business_name || l.contact_name}</div>
              <div className="text-xs text-blue-400 mt-0.5">
                {l.next_follow_up ? new Date(l.next_follow_up).toLocaleString() : "—"}
              </div>
            </Link>
          )}
        />
        <ListPanel
          title="Recent audit requests"
          testid="recent-audits"
          empty="No audit requests yet."
          items={data.audit_requests}
          renderItem={(l) => (
            <Link to={`/admin/dashboard/leads/${l.id}`} className="block">
              <div className="text-sm text-white truncate">{l.business_name}</div>
              <div className="text-xs text-white/45 mt-0.5">{l.website || l.email}</div>
            </Link>
          )}
        />
      </div>
    </div>
  );
}

function ListPanel({ title, items, empty, renderItem, testid }) {
  return (
    <div className="card-soft rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg text-white">{title}</h3>
        <ArrowUpRight size={14} className="text-white/30" />
      </div>
      {!items || items.length === 0 ? (
        <div className="text-sm text-white/40">{empty}</div>
      ) : (
        <ul className="space-y-3">
          {items.map((it, i) => (
            <li key={it.id || i} className="border-t border-white/5 pt-3 first:border-t-0 first:pt-0">
              {renderItem(it)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="h-[240px] flex items-center justify-center text-sm text-white/40 text-center px-6">
      {label}
    </div>
  );
}
